// domain 이 shared 에 기대므로 domain 의 ApiBoundaryIssue 를 가져오지 않고 같은 모양만 받는다.
type ApiBoundaryIssue = {
  entity: string
  issues: ReadonlyArray<{ path: ReadonlyArray<PropertyKey>; message: string }>
}

const ROOT_ID = 'mathflat-api-boundary-overlay'

type Entry = {
  entity: string
  lines: string[]
  count: number
}

const C = {
  bg: '#1a0f0f',
  border: 'rgba(239,68,68,0.45)',
  fg: '#fde8e8',
  fgDim: '#f3a5a5',
  accent: '#ef4444',
  mono: "ui-monospace,'JetBrains Mono','SF Mono',Menlo,monospace",
  font: "ui-sans-serif,-apple-system,BlinkMacSystemFont,'Pretendard',system-ui,sans-serif",
}

const entries = new Map<string, Entry>()

const toLines = ({ issues }: ApiBoundaryIssue) =>
  issues.map((issue) => `${issue.path.map(String).join('.') || '(root)'}: ${issue.message}`)

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  css: string,
  text?: string,
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag)
  node.style.cssText = css
  if (text !== undefined) node.textContent = text
  return node
}

const entryTitle = ({ entity, count }: Entry) => (count > 1 ? `${entity} ×${count}` : entity)

/** 이슈 트래커·메신저에 그대로 붙여 넣을 수 있는 평문으로 만든다. */
const formatApiBoundaryLog = () =>
  [
    `API 응답이 스키마와 다릅니다 (${entries.size}건)`,
    `페이지: ${location.href}`,
    ...[...entries.values()].map(
      (entry) => `\n${entryTitle(entry)}\n${entry.lines.map((line) => `  ${line}`).join('\n')}`,
    ),
  ].join('\n')

// 비보안 컨텍스트(HTTP 로 연 dev 서버 등)에는 navigator.clipboard 가 없어 execCommand 로 물러선다.
const copyText = async (text: string) => {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text)
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.cssText = 'position:fixed;opacity:0'
  document.body.appendChild(textarea)
  textarea.select()
  const copied = document.execCommand('copy')
  textarea.remove()
  return copied ? undefined : Promise.reject(new Error('클립보드 복사 실패'))
}

const render = () => {
  // React 트리가 깨져도 보이도록 body 에 직접 붙인다.
  let root = document.getElementById(ROOT_ID)
  if (!root) {
    root = el(
      'div',
      `position:fixed;left:12px;bottom:12px;z-index:2147483001;width:min(560px,calc(100vw - 24px));` +
        `max-height:50vh;display:flex;flex-direction:column;background:${C.bg};color:${C.fg};` +
        `border:1px solid ${C.border};border-radius:10px;box-shadow:0 10px 30px rgba(0,0,0,0.45);` +
        `font:12px/1.5 ${C.font}`,
    )
    root.id = ROOT_ID
    document.body.appendChild(root)
  }
  root.replaceChildren()

  const header = el(
    'div',
    `display:flex;align-items:center;gap:8px;padding:8px 12px;border-bottom:1px solid ${C.border}`,
  )
  header.appendChild(
    el('strong', `flex:1;color:${C.accent}`, `API 응답이 스키마와 다릅니다 (${entries.size}건)`),
  )
  const copy = el(
    'button',
    `all:unset;cursor:pointer;padding:2px 8px;border:1px solid ${C.border};border-radius:6px;` +
      `color:${C.fg};font-size:11px`,
    '로그 복사',
  )
  copy.addEventListener('click', () => {
    copyText(formatApiBoundaryLog()).then(
      () => (copy.textContent = '복사됨'),
      () => (copy.textContent = '복사 실패'),
    )
  })
  header.appendChild(copy)
  const close = el(
    'button',
    `all:unset;cursor:pointer;padding:0 4px;color:${C.fgDim};font-size:14px`,
    '✕',
  )
  close.setAttribute('aria-label', '스키마 오류 오버레이 닫기')
  close.addEventListener('click', clearApiBoundaryOverlay)
  header.appendChild(close)
  root.appendChild(header)

  const body = el('div', 'overflow:auto;padding:8px 12px')
  for (const entry of entries.values()) {
    const section = el('div', 'margin-bottom:8px')
    section.appendChild(el('div', 'font-weight:600', entryTitle(entry)))
    section.appendChild(
      el(
        'pre',
        `margin:2px 0 0;white-space:pre-wrap;word-break:break-all;color:${C.fgDim};font:11px/1.5 ${C.mono}`,
        entry.lines.join('\n'),
      ),
    )
    body.appendChild(section)
  }
  root.appendChild(body)
}

/** 어긋난 응답을 화면 구석에 쌓아 보여 준다. 같은 엔티티의 같은 어긋남은 한 줄로 합친다. */
export const showApiBoundaryIssue = (issue: ApiBoundaryIssue) => {
  if (typeof document === 'undefined') return
  const lines = toLines(issue)
  const key = `${issue.entity}\n${lines.join('\n')}`
  const prev = entries.get(key)
  entries.set(key, { entity: issue.entity, lines, count: (prev?.count ?? 0) + 1 })
  render()
}

export const clearApiBoundaryOverlay = () => {
  entries.clear()
  document.getElementById(ROOT_ID)?.remove()
}
